import { api } from './index'

import type { CertificateActionLogType } from './certificateActivityApi'

/**
 * The company's CERTIFICATE activity — every scan, transfer and issuance across every
 * certificate the company owns (VRG-338). Powers the Home page's Recent activity block.
 *
 * ⚠️ NOT THE SAME THING AS `companyActivityApi`, and the near-identical names are a real
 * hazard, so the difference is spelled out here rather than left to be discovered:
 *
 *   companyActivityApi            → GET /companies/admin/me/activity
 *                                   the ADMIN AUDIT LOG: company_updated, user_added,
 *                                   permissions_updated, role_added, payment_method_updated.
 *                                   Who changed the SETTINGS. Powers Settings → Activity Log.
 *
 *   companyCertificateActivityApi → GET /action-log/company
 *                                   what happened to the PRODUCTS: scans, transfers,
 *                                   certificate creation — including actions taken by
 *                                   customers and anonymous visitors, who never appear in
 *                                   the audit log at all.
 *
 * Home wants the second one. An audit log answers "who changed our configuration", which is
 * a compliance question; Home is asking "what happened to our things", which is news.
 */
export interface CompanyCertificateActivityActor {
  id: string
  first_name: string | null
  last_name: string | null
  email: string
}

export interface CompanyCertificateActivityItem {
  id: string
  type: CertificateActionLogType
  created_at: string
  user_type: 'admin' | 'customer' | 'anonymous'
  /**
   * Resolved server-side, and ONLY for admin actors. Null for customer and anonymous ones —
   * which is most of the feed, since scans are overwhelmingly anonymous.
   */
  actor: CompanyCertificateActivityActor | null
  /** `name` is the PRODUCT's title; a certificate has no name of its own. */
  certificate: { id: string; name: string | null }
}

export interface CompanyCertificateActivityParams {
  limit?: number
  offset?: number
  /** Narrow to one actor's own actions ("just mine") instead of the whole company's. */
  actorUserId?: string
}

export interface CompanyCertificateActivityResponse {
  data: CompanyCertificateActivityItem[]
  total: number
  limit: number
  offset: number
}

export const companyCertificateActivityApi = {
  async getCompanyCertificateActivity(
    params?: CompanyCertificateActivityParams
  ): Promise<CompanyCertificateActivityResponse> {
    // No company id in the path — the server resolves it from the session, so a caller
    // cannot read another company's feed by editing a parameter.
    const response = await api.get('/action-log/company', { params })
    return response.data
  },
}
